import { useIntl } from 'react-intl';

import { additionalDetailsSectionMessages } from './additional-details-section-messages';
import styles from './AdditionalDetailsSection.module.css';
import { DetailSection } from '../detail-section/DetailSection';
import { MeasurementCell } from '../measurement-cell/MeasurementCell';

import type { LtlShipment } from '../../../../types/ltl';

type AdditionalDetailsSectionProps = {
  shipment: LtlShipment;
  currencyCode?: string;
};

const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * Left-column Additional Details card. Surfaces the first commodity's insurance
 * (the only real data today) plus the shipment reference number. Most insurance
 * sub-fields and the order number are §7.4 data gaps and are conditionally
 * omitted. Store-agnostic — props only.
 */
export const AdditionalDetailsSection = ({ shipment, currencyCode = DEFAULT_CURRENCY_CODE }: AdditionalDetailsSectionProps) => {
  const intl = useIntl();

  // Insurance is not on LtlShipment, but LtlCommodity carries an `insurance` object.
  // Surface the first commodity's insurance when present (§7.4 partial fill).
  const insurance = shipment.handlingUnits[0]?.commodities[0]?.insurance ?? null;
  const hasInsurance = insurance !== null && insurance.isInsured;

  return (
    <DetailSection testId='additional-details-section' title={intl.formatMessage(additionalDetailsSectionMessages.sectionTitle)}>
      <div className={styles.groups}>
        {hasInsurance && (
          <div className={styles.group}>
            <span className={styles.groupTitle}>{intl.formatMessage(additionalDetailsSectionMessages.insuranceTitle)}</span>
            <div className={styles.measurementRow}>
              <MeasurementCell label={intl.formatMessage(additionalDetailsSectionMessages.conditionOfGoods)} value={insurance.condition} />
              <MeasurementCell
                label={intl.formatMessage(additionalDetailsSectionMessages.category)}
                value={insurance.commodityCategoryCode}
              />
              <MeasurementCell
                label={intl.formatMessage(additionalDetailsSectionMessages.insuredValue)}
                value={intl.formatNumber(insurance.declaredValue, { style: 'currency', currency: currencyCode })}
              />
              {/* §7.4 data gap: Marks & Numbers — no field on LtlShipment or LtlInsurance, omitted. */}
            </div>
          </div>
        )}

        <div className={styles.group}>
          <span className={styles.groupTitle}>{intl.formatMessage(additionalDetailsSectionMessages.referencesTitle)}</span>
          <div className={styles.measurementRow}>
            <MeasurementCell
              label={intl.formatMessage(additionalDetailsSectionMessages.referencesTitle)}
              value={intl.formatMessage(additionalDetailsSectionMessages.shipmentNumber, { number: shipment.freightShipmentId })}
            />
            {/* §7.4 data gap: Order # — no order number field on LtlShipment, omitted. */}
          </div>
        </div>
      </div>
    </DetailSection>
  );
};
